/**
 * Customer Service Response Processor
 *
 * Specialized processor for Customer Service agent.
 * Extends DefaultProcessor with human handoff handling.
 *
 * @module services/responseProcessors/CustomerServiceProcessor
 */

import {
  IResponseProcessor,
  NormalizedResponse,
  ProcessingResult,
  ProcessorContext,
  ChatMessage,
  LiveChatButtonContent,
} from './types';
import { ChatMessageResponse } from '@/collections/chat';
import { DefaultProcessor } from './DefaultProcessor';

/**
 * Processor for Customer Service agent
 *
 * Extends DefaultProcessor with:
 * - Live chat handoff action handling
 * - Live chat button rendering (structured content)
 */
export class CustomerServiceProcessor implements IResponseProcessor {
  readonly agentIds = ['customer-service'];
  readonly description = 'Processor for Customer Service with human handoff support';

  private defaultProcessor = new DefaultProcessor();

  /**
   * Use default normalization
   */
  normalizeResponse(response: ChatMessageResponse): NormalizedResponse {
    return this.defaultProcessor.normalizeResponse(response);
  }

  /**
   * Process response with Customer Service specific handoff handling
   */
  async processResponse(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): Promise<ProcessingResult> {
    // Get base processing from default processor
    const result = await this.defaultProcessor.processResponse(normalized, context);

    // Handle action events for live chat
    const actionEvent = normalized.events.find((e) => e.type === 'action');

    if (actionEvent?.metadata?.action === 'live_chat_handoff') {
      // Auto-trigger live chat (immediate handoff)
      result.eventBusEmits!.push({
        event: 'open-crisp-for-handoff',
        payload: {
          agentId: context.selectedAgent.id,
          agentName: context.selectedAgent.name,
          conversationId: context.conversationId,
          metadata: {
            handoff_reason: actionEvent.metadata.reason || 'user_request',
            handoff_count: actionEvent.metadata.handoff_count || 1,
          },
          timestamp: new Date().toISOString(),
        },
      });
    } else if (actionEvent?.metadata?.action === 'show_live_chat_button') {
      // Add message with live chat button (user chooses when to connect)
      const buttonContent: LiveChatButtonContent = {
        type: 'live_chat_button',
        buttonText: String(
          actionEvent.metadata.button_text || 'Connect to Live Support'
        ),
        buttonDescription: String(
          actionEvent.metadata.button_description ||
            'Click to connect with our support team'
        ),
        ticketKey: actionEvent.metadata.ticket_key as string | undefined,
        metadata: {
          ...actionEvent.metadata,
          ticket_created: actionEvent.metadata.ticket_created || false,
        },
      };

      const buttonMessage: ChatMessage = {
        id: context.generateMessageId(),
        content: buttonContent.buttonText,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: 'System',
          avatar: 'mdi-human-greeting',
        },
        structuredContent: [buttonContent],
      };

      result.messagesToAdd.push(buttonMessage);
    }

    // Also check metadata for handoff flags (legacy support)
    if (
      normalized.metadata.needs_human_handoff &&
      !result.eventBusEmits?.some((e) => e.event === 'open-crisp-for-handoff')
    ) {
      result.eventBusEmits!.push({
        event: 'open-crisp-for-handoff',
        payload: {
          agentId: context.selectedAgent.id,
          agentName: context.selectedAgent.name,
          conversationId: context.conversationId,
          metadata: {
            handoff_reason: normalized.metadata.handoff_reason || 'agent_triggered',
          },
          timestamp: new Date().toISOString(),
        },
      });
    }

    return result;
  }
}
